"use client"

import { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { TemplateForm } from './template-form'
import { UserTemplate } from '@/supabase/types'
import { useLanguage } from "@/contexts/language-context"
import { useCommunicationOptions } from "@/hooks/use-communication-options"

interface TemplateModalProps {
  isOpen: boolean
  onClose: () => void
}

export function TemplateModal({ isOpen, onClose }: TemplateModalProps) {
  const { t } = useLanguage()
  const { getScenarioLabel, getTargetLabel, getToneLabel } = useCommunicationOptions()
  const [templates, setTemplates] = useState<UserTemplate[]>([])
  const [editing, setEditing] = useState<UserTemplate | null>(null)
  const [isLoading, setIsLoading] = useState(false)



  const fetchTemplates = async () => {
    setIsLoading(true)
    try {
      const response = await fetch('/api/templates', { credentials: 'include' })
      if (!response.ok) throw new Error('Failed to load templates')
      const data = await response.json()
      setTemplates(data.templates || [])
    } catch (err) {
      console.error(err)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (isOpen) {
      fetchTemplates()
    }
  }, [isOpen])

  const handleSaved = (tpl: UserTemplate) => {
    setEditing(null)
    const exists = templates.find(t => t.id === tpl.id)
    if (exists) {
      setTemplates(prev => prev.map(t => (t.id === tpl.id ? tpl : t)))
    } else {
      setTemplates(prev => [tpl, ...prev])
    }
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('templates-changed'))
    }
  }

  const handleDeleted = (id: string) => {
    setTemplates(prev => prev.filter(t => t.id !== id))
    setEditing(null)
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('templates-changed'))
    }
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          if (editing) {
            setEditing(null)
          } else {
            onClose()
          }
        }
      }}
    >
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto bg-gray-color">
        <DialogHeader>
          <DialogTitle>{editing ? 'Template Details' : 'My Templates'}</DialogTitle>
          <DialogDescription>
            {editing ? 'Edit your template' : 'Manage your templates'}
          </DialogDescription>
        </DialogHeader>
        {editing ? (
          <TemplateForm template={editing.id ? editing : undefined} onCancel={() => setEditing(null)} onSaved={handleSaved} onDelete={handleDeleted} />
        ) : (
          <div className="space-y-4 bg-zinc-color p-4 rounded-lg">
            <Button className="bg-card-color" onClick={() => setEditing({} as UserTemplate)}>New Template</Button>
            {isLoading ? (
              <div>Loading...</div>
            ) : templates.length === 0 ? (
              <div>No templates found</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {templates.map(t => (
                  <Card key={t.id} className="hover-glow-card cursor-pointer bg-card-color transition-shadow" onClick={() => setEditing(t)}>
                    <CardContent className="p-4 space-y-2">
                      <div className="font-semibold line-clamp-1">{t.name}</div>
                      <div className="text-sm text-gray-500 line-clamp-3 whitespace-pre-wrap">{t.content}</div>
                      <div className="flex flex-wrap gap-1 pt-1">
                        {t.scenario && (
                          <span className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded">
                            {getScenarioLabel(t.scenario)}
                          </span>
                        )}
                        {t.target && (
                          <span className="px-2 py-1 text-xs bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded">
                            {getTargetLabel(t.target)}
                          </span>
                        )}
                        {t.tone && (
                          <span className="px-2 py-1 text-xs bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 rounded">
                            {getToneLabel(t.tone)}
                          </span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

